import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as authApi from '../auth.api';

import { useAuthStore } from '@/stores/auth-store';

/**
 * Inactivity auto-logout engine for the CRM.
 *
 * Design (why it is built this way):
 *
 * - TIMESTAMP-BASED, not countdown-based. A single shared "last activity"
 *   epoch-ms value lives in `localStorage` under one key. Every tab writes it
 *   on real user activity and every tab reads it on its tick, so activity in
 *   ANY tab keeps EVERY tab alive. This is the whole of multi-tab correctness:
 *   there is one clock, shared, not one timer per tab that can drift apart.
 *
 * - A single ~1s interval (plus an immediate re-check on tab focus /
 *   visibility) does pure arithmetic: `elapsed = now - lastActivity`. Because
 *   it is arithmetic on a stored timestamp, it is immune to background-tab
 *   timer throttling - whenever the interval next fires (or the tab is
 *   refocused) the elapsed time is exact, never stale. No per-event timers to
 *   leak or reset.
 *
 * - ZERO server calls while idle. The engine is entirely client-side. The only
 *   network calls are: one `/auth/refresh` when the user clicks Extend, and one
 *   `/auth/logout` when the session actually ends.
 *
 * - Cross-tab coordination uses a BroadcastChannel for TWO rare events only:
 *   `extend` (reset + close the dialog everywhere) and `logout` (all tabs end
 *   together). There is no activity chatter on the channel - ordinary activity
 *   rides the shared localStorage timestamp, so messaging is near-zero.
 *
 * - Once the warning is showing (elapsed >= WARN), activity is FROZEN: a stray
 *   mouse move will not silently reset it. Only the explicit "Extend session"
 *   button (in any tab) - or genuine activity in another tab that is still in
 *   its active window - clears it. This is the deliberate "no auto close"
 *   requirement: the user must act, not merely twitch the mouse.
 */

/** Shared cross-tab "last activity" clock. Exported for tests. */
export const IDLE_STORAGE_KEY = 'utazzo.idle.lastActivity';

/**
 * Set right before an inactivity logout so the login page can tell the user
 * WHY they were signed out. Session-scoped (survives the redirect + a reload,
 * clears when the browser session ends); the login page also clears it on
 * dismiss and on a successful sign-in.
 */
export const IDLE_LOGGED_OUT_KEY = 'utazzo.idle.loggedOutInactive';
const CHANNEL_NAME = 'utazzo.idle';

/** 10 min of inactivity -> warning dialog. */
export const WARN_AFTER_MS = 10 * 60 * 1000;
/** 15 min total (5 min after the warning) -> auto logout. */
export const LOGOUT_AFTER_MS = 15 * 60 * 1000;

/**
 * ADMIN gets a far longer leash. Admins run long back-office sessions (reports,
 * reconciliation, bulk imports) where a 10-min idle logout is disruptive, so for
 * the ADMIN role only it is 2 h of inactivity -> warning, then the same 5-min
 * warning window -> logout. Every other role keeps the standard 10/15 min.
 */
export const ADMIN_WARN_AFTER_MS = 2 * 60 * 60 * 1000;
export const ADMIN_LOGOUT_AFTER_MS = ADMIN_WARN_AFTER_MS + 5 * 60 * 1000;

const TICK_MS = 1000;
/** Never touch localStorage more than once per this window on activity. */
const WRITE_THROTTLE_MS = 2000;

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
] as const;

type Phase = 'active' | 'warning';
type ChannelMessage = { type: 'extend'; at: number } | { type: 'logout' };

const nowMs = (): number => Date.now();

const readLastActivity = (): number => {
  const raw = localStorage.getItem(IDLE_STORAGE_KEY);
  const parsed = raw ? Number(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : nowMs();
};

const writeLastActivity = (ts: number): void => {
  localStorage.setItem(IDLE_STORAGE_KEY, String(ts));
};

/** DEV-only. Log idle diagnostics to the console. */
const DEBUG = import.meta.env.DEV;

/**
 * DEV-only fast-test override. Set in the browser console to shrink the
 * windows so you don't have to wait 10 minutes to test:
 *   localStorage['utazzo.idle.testWarnSec'] = '10'
 *   localStorage['utazzo.idle.testLogoutSec'] = '20'
 * then reload. In production these keys are ignored - the real 10/15 min apply.
 */
const resolveThresholds = (isAdmin: boolean): { warn: number; logout: number } => {
  const warnBase = isAdmin ? ADMIN_WARN_AFTER_MS : WARN_AFTER_MS;
  const logoutBase = isAdmin ? ADMIN_LOGOUT_AFTER_MS : LOGOUT_AFTER_MS;
  if (!import.meta.env.DEV) return { warn: warnBase, logout: logoutBase };
  const warnSec = Number(localStorage.getItem('utazzo.idle.testWarnSec'));
  const logoutSec = Number(localStorage.getItem('utazzo.idle.testLogoutSec'));
  return {
    warn: Number.isFinite(warnSec) && warnSec > 0 ? warnSec * 1000 : warnBase,
    logout: Number.isFinite(logoutSec) && logoutSec > 0 ? logoutSec * 1000 : logoutBase,
  };
};

/**
 * Decide whether a DOM event is genuine user activity that should keep the
 * session alive. Pure + exported so the rule is unit-testable without the
 * un-forgeable `isTrusted` that jsdom blocks in the full-hook path.
 *
 *  - Synthetic (script-dispatched) events are ignored - only real input counts.
 *  - A `mousemove` that did not actually move the pointer is ignored (the
 *    browser fires it when content scrolls/animates under a stationary cursor).
 *  - Everything else trusted (keydown, mousedown, scroll, wheel, touch) counts.
 *
 * Returns the verdict plus the cursor position to remember for the next call.
 */
export const classifyActivity = (
  e: { type: string; isTrusted: boolean; clientX?: number; clientY?: number },
  lastX: number,
  lastY: number,
): { isActivity: boolean; lastX: number; lastY: number } => {
  if (!e.isTrusted) return { isActivity: false, lastX, lastY };
  if (e.type === 'mousemove') {
    if (e.clientX === lastX && e.clientY === lastY) return { isActivity: false, lastX, lastY };
    return { isActivity: true, lastX: e.clientX ?? lastX, lastY: e.clientY ?? lastY };
  }
  return { isActivity: true, lastX, lastY };
};

export interface IdleLogoutState {
  phase: Phase;
  /** ms left before auto-logout - only meaningful while `phase === 'warning'`. */
  remainingMs: number;
  /** Explicit "keep me signed in": reset the shared clock + refresh the token. */
  extend: () => void;
  extending: boolean;
}

export const useIdleLogout = (): IdleLogoutState => {
  const navigate = useNavigate();
  // ADMIN runs on the 2 h timeout, every other role on the standard 10/15 min.
  // Subscribed (not read once) so a role change re-arms the engine's thresholds.
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const [phase, setPhase] = useState<Phase>('active');
  const [remainingMs, setRemainingMs] = useState(LOGOUT_AFTER_MS - WARN_AFTER_MS);
  const [extending, setExtending] = useState(false);

  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastWriteRef = useRef(0);
  // Guards logout against being fired twice (tick + broadcast + focus can race).
  const loggingOutRef = useRef(false);
  // Reports the "idle warning shown" audit event once per warning episode.
  const warningReportedRef = useRef(false);

  /** Best-effort server logout, local session clear, redirect - once only. */
  const performLogout = useCallback(
    (broadcast: boolean) => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      if (broadcast) {
        channelRef.current?.postMessage({ type: 'logout' } satisfies ChannelMessage);
      }
      // Deliberately do NOT remove the shared idle clock here: another tab that
      // missed the broadcast must still read a STALE timestamp so its own tick
      // logs it out too. Clearing it would make readLastActivity() fall back to
      // "now" and that tab would look active. It's overwritten on next login.
      // Tell the login page why the user landed there.
      sessionStorage.setItem(IDLE_LOGGED_OUT_KEY, '1');
      // Fire-and-forget - never block the redirect on the network. The reason
      // tags the audit trail as an inactivity logout, not a manual sign-out.
      authApi.logout('inactivity').catch(() => undefined);
      useAuthStore.getState().clear();
      navigate('/login', { replace: true });
    },
    [navigate],
  );

  /** Extend: reset the shared clock everywhere, then renew the token. */
  const extend = useCallback(() => {
    const ts = nowMs();
    writeLastActivity(ts);
    lastWriteRef.current = ts;
    setPhase('active');
    channelRef.current?.postMessage({ type: 'extend', at: ts } satisfies ChannelMessage);
    // Audit-only, best-effort.
    authApi.recordSessionEvent('idle_extended').catch(() => undefined);

    const refreshToken = useAuthStore.getState().tokens?.refreshToken;
    if (!refreshToken) return;
    setExtending(true);
    authApi
      .refresh(refreshToken)
      .then((tokens) => useAuthStore.getState().setTokens(tokens))
      // If the refresh token itself is dead, the session is genuinely over.
      .catch(() => performLogout(true))
      .finally(() => setExtending(false));
  }, [performLogout]);

  useEffect(() => {
    const { warn: warnMs, logout: logoutMs } = resolveThresholds(isAdmin);

    // A fresh mount (login or reload) is a deliberate present-user action -
    // start the clock now so a stale timestamp can't trip an instant logout.
    const startTs = nowMs();
    writeLastActivity(startTs);
    lastWriteRef.current = startTs;

    // Tracks the last real cursor position - `mousemove` fires even when the
    // pointer is stationary (e.g. the page scrolls or content animates under
    // it), which would silently re-arm the timer forever. We ignore those.
    let lastX = Number.NaN;
    let lastY = Number.NaN;
    let lastLogSec = -1;

    const channel =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
    channelRef.current = channel;
    if (channel) {
      channel.onmessage = (e: MessageEvent<ChannelMessage>) => {
        const msg = e.data;
        if (msg?.type === 'extend') {
          setPhase('active');
        } else if (msg?.type === 'logout') {
          performLogout(false);
        }
      };
    }

    // Activity resets the shared clock - but throttle localStorage writes, and
    // FREEZE once we are inside the warning window (only Extend clears it).
    const onActivity = (e: Event): void => {
      const me = e as MouseEvent;
      const verdict = classifyActivity(
        { type: e.type, isTrusted: e.isTrusted, clientX: me.clientX, clientY: me.clientY },
        lastX,
        lastY,
      );
      lastX = verdict.lastX;
      lastY = verdict.lastY;
      if (!verdict.isActivity) return;
      const ts = nowMs();
      if (ts - lastWriteRef.current < WRITE_THROTTLE_MS) return; // cheap throttle first
      if (ts - readLastActivity() >= warnMs) return; // in warning -> frozen
      writeLastActivity(ts);
      lastWriteRef.current = ts;
      // eslint-disable-next-line no-console -- DEV-only trace (DEBUG = import.meta.env.DEV)
      if (DEBUG) console.debug('[idle] clock reset by', e.type);
    };
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    // Single source of truth for the state machine.
    const evaluate = (): void => {
      if (loggingOutRef.current) return;
      const elapsed = nowMs() - readLastActivity();
      if (DEBUG) {
        const sec = Math.floor(elapsed / 1000);
        if (sec !== lastLogSec && sec % 5 === 0) {
          lastLogSec = sec;
          // eslint-disable-next-line no-console -- DEV-only trace (DEBUG = import.meta.env.DEV)
          console.debug(
            `[idle] idle ${sec}s (warn @ ${warnMs / 1000}s, logout @ ${logoutMs / 1000}s)`,
          );
        }
      }
      if (elapsed >= logoutMs) {
        performLogout(true);
        return;
      }
      if (elapsed >= warnMs) {
        setPhase('warning');
        setRemainingMs(logoutMs - elapsed);
        if (!warningReportedRef.current) {
          warningReportedRef.current = true;
          // Audit-only, best-effort - record that the warning was shown.
          authApi.recordSessionEvent('idle_warning').catch(() => undefined);
        }
      } else {
        setPhase('active');
        warningReportedRef.current = false;
      }
    };
    // DEV helper: call `__idle()` in the console to read current idle seconds.
    if (DEBUG) {
      (window as unknown as { __idle?: () => number }).__idle = () =>
        Math.round((nowMs() - readLastActivity()) / 1000);
    }

    const interval = window.setInterval(evaluate, TICK_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') evaluate();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', evaluate);
    evaluate();

    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', evaluate);
      channel?.close();
      channelRef.current = null;
    };
  }, [performLogout, isAdmin]);

  return { phase, remainingMs, extend, extending };
};
